import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaSearch, FaTasks, FaProjectDiagram, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '../hooks/useGlobalSearch';

const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();
  const bg = useColorModeValue('white', 'gray.800');
  const { data, isFetching } = useGlobalSearch(debounced);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const hasResults =
    data &&
    (data.tasks.length > 0 || data.projects.length > 0 || data.clients.length > 0);

  const go = (href: string) => {
    setQuery('');
    setDebounced('');
    navigate(href);
  };

  return (
    <Popover isOpen={debounced.length >= 2} placement="bottom-start" autoFocus={false}>
      <PopoverTrigger>
        <Box w={{ base: 'full', md: '280px' }}>
          <InputGroup size="sm">
            <InputLeftElement>
              <Icon as={FaSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search tasks, projects, clients..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              borderRadius="full"
              bg={bg}
            />
          </InputGroup>
        </Box>
      </PopoverTrigger>
      <PopoverContent w="360px" shadow="lg">
        <PopoverBody p={0}>
          {isFetching ? (
            <Box p={4} textAlign="center">
              <Spinner size="sm" />
            </Box>
          ) : !hasResults ? (
            <Text p={4} fontSize="sm" color="gray.500">
              No results for &quot;{debounced}&quot;
            </Text>
          ) : (
            <VStack align="stretch" spacing={0} maxH="320px" overflowY="auto">
              {data!.tasks.map((item) => (
                <HStack
                  key={item._id}
                  px={4}
                  py={2}
                  _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                  onClick={() => go(item.href)}
                >
                  <Icon as={FaTasks} color="orange.400" />
                  <Box>
                    <Text fontSize="sm" fontWeight="medium">
                      {item.label}
                    </Text>
                    {item.sublabel && (
                      <Text fontSize="xs" color="gray.500">
                        {item.sublabel}
                      </Text>
                    )}
                  </Box>
                </HStack>
              ))}
              {data!.projects.map((item) => (
                <HStack
                  key={item._id}
                  px={4}
                  py={2}
                  _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                  onClick={() => go(item.href)}
                >
                  <Icon as={FaProjectDiagram} color="green.400" />
                  <Box>
                    <Text fontSize="sm" fontWeight="medium">
                      {item.label}
                    </Text>
                    {item.sublabel && (
                      <Text fontSize="xs" color="gray.500">
                        {item.sublabel}
                      </Text>
                    )}
                  </Box>
                </HStack>
              ))}
              {data!.clients.map((item) => (
                <HStack
                  key={item._id}
                  px={4}
                  py={2}
                  _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                  onClick={() => go(item.href)}
                >
                  <Icon as={FaUsers} color="purple.400" />
                  <Text fontSize="sm" fontWeight="medium">
                    {item.label}
                  </Text>
                </HStack>
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default GlobalSearch;
